import {
    createScopeClauseFeedback,
    deleteFeedbackById,
    FeedbackType,
    getScopeClauseFeedback,
    IFeedback,
    IScopeClause,
    Vote,
} from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import { styles } from "@/config/project";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { AddFeedbackDialog } from "./feedback-dialog/AddFeedbackDialog";

interface IProps {
    /**
     * The scope clause to display feedback for.
     */
    scopeClause: IScopeClause;
    /**
     * The page navigation plugin instance
     */
    pageNavigationPluginInstance?: PageNavigationPlugin;
    /**
     * Whether the scope clause is expanded
     */
    expanded: boolean;
    /**
     * Whether this chip is highlighted (from PDF click)
     */
    isHighlighted?: boolean;
    /**
     * Optional callback when page number is clicked (for highlighting clause on PDF)
     */
    onPageClick?: () => void;
}

/**
 * Displays a feedback chip representing a scope clause section that gives the users the ability to upvote or downvote the scope clause.
 */
export const FeedbackChip = ({
    scopeClause,
    pageNavigationPluginInstance,
    expanded,
    isHighlighted = false,
    onPageClick,
}: IProps) => {
    const [feedback, setFeedback] = useState<IFeedback | undefined>();

    const queryClient = useQueryClient();
    const { data: feedbackData, isPending } = useQuery({
        queryKey: ["frontend-feedback", scopeClause.id],
        queryFn: () =>
            getScopeClauseFeedback(scopeClause.scope_id, scopeClause.id),
        retry: false,
        enabled: expanded,
    });

    const {
        mutate: deleteFeedback,
        isPending: feedbackDeletePending,
        isError: isDeleteError,
    } = useMutation({
        mutationFn: async (feedbackId: number) => {
            return deleteFeedbackById(feedbackId);
        },
        onSuccess: () => {
            setFeedback(undefined);
            queryClient.invalidateQueries({
                queryKey: ["frontend-feedback", scopeClause.id],
            });
        },
        onError: () => {
            enqueueSnackbar("An error occurred while deleting feedback", {
                variant: "error",
            });
        },
    });

    const { mutate: createUpvote } = useMutation({
        mutationFn: async () => {
            return createScopeClauseFeedback(
                scopeClause.scope_id,
                scopeClause.id,
                {
                    vote: Vote.UP,
                    feedback_type: FeedbackType.OTHER,
                    additional_comments: "",
                }
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-feedback", scopeClause.id],
            });
        },
        onError: () => {
            enqueueSnackbar("An error occurred while submitting feedback", {
                variant: "error",
            });
        },
    });

    const dialogs = useDialogs();

    const handleVote = async (vote: Vote) => {
        // If already feedback exists, delete it
        if (feedback) {
            deleteFeedback(feedback.id);

            // if the vote is the same, don't continue
            if (feedback.vote === vote) {
                return;
            }
        }

        if (!isDeleteError && vote === Vote.DOWN) {
            // handle downvote
            await dialogs.open(AddFeedbackDialog, {
                scopeClause,
                vote,
                onSucceeded: () => {
                    queryClient.invalidateQueries({
                        queryKey: ["frontend-feedback", scopeClause.id],
                    });
                },
            });
        } else if (!isDeleteError && vote === Vote.UP) {
            // handle upvote
            createUpvote();
        }
    };

    const handleJumpToHighlight = () => {
        if (scopeClause.highlight_areas.length > 0) {
            pageNavigationPluginInstance?.jumpToPage(
                scopeClause.highlight_areas[0].page_index
            );
            // Trigger highlight on PDF if callback provided
            onPageClick?.();
        }
    };

    useEffect(() => {
        if (feedbackData) setFeedback(feedbackData);
        else setFeedback(undefined);
    }, [feedbackData]);

    return (
        <Box
            bgcolor={isHighlighted ? "primary.main" : "#EBEBEB"}
            pl={1}
            pr={0.5}
            py={0.5}
            borderRadius={25}
            sx={{
                transition: "background-color 0.2s ease",
                ...(isHighlighted && {
                    boxShadow: (theme) =>
                        `0 0 0 2px ${theme.palette.primary.main}`,
                }),
            }}
        >
            <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Typography
                    variant="caption"
                    flexShrink={0}
                    component={"a"}
                    onClick={handleJumpToHighlight}
                    sx={{
                        textDecoration: scopeClause.found
                            ? "underline"
                            : "none",
                        cursor: scopeClause.found ? "pointer" : "default",
                        color: isHighlighted
                            ? "primary.contrastText"
                            : "text.primary",
                    }}
                >
                    {scopeClause.found &&
                        scopeClause.highlight_areas.length > 0 &&
                        `Page ${scopeClause.highlight_areas[0].page_index + 1}`}

                    {scopeClause.found &&
                        scopeClause.highlight_areas.length === 0 &&
                        "Unknown"}

                    {!scopeClause.found && "Not Found"}
                </Typography>

                <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
                    {isPending && (
                        <Skeleton
                            variant={"circular"}
                            sx={{ bgcolor: styles.successColor }}
                            width={28}
                            height={28}
                        />
                    )}
                    {!isPending && (
                        <Button
                            disabled={feedbackDeletePending}
                            onClick={() => handleVote(Vote.UP)}
                            size={"small"}
                            sx={{
                                p: 0.625,
                                borderRadius: "100%",
                                minWidth: 0,
                            }}
                            variant="contained"
                            color={
                                feedback && feedback.vote === Vote.UP
                                    ? "thumbsUpOn"
                                    : "thumbsUpOff"
                            }
                            disableElevation
                        >
                            <ThumbUpOutlinedIcon
                                sx={{
                                    height: 18,
                                    width: 18,
                                }}
                            />
                        </Button>
                    )}

                    {isPending && (
                        <Skeleton
                            variant={"circular"}
                            sx={{ bgcolor: styles.errorColor }}
                            width={28}
                            height={28}
                        />
                    )}
                    {!isPending && (
                        <Button
                            disabled={feedbackDeletePending}
                            onClick={() => handleVote(Vote.DOWN)}
                            size={"small"}
                            sx={{
                                p: 0.625,
                                borderRadius: "100%",
                                minWidth: 0,
                            }}
                            variant="contained"
                            color={
                                feedback && feedback.vote === Vote.DOWN
                                    ? "thumbsDownOn"
                                    : "thumbsDownOff"
                            }
                            disableElevation
                        >
                            <ThumbDownOutlinedIcon
                                sx={{
                                    height: 18,
                                    width: 18,
                                }}
                            />
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};
